import React, { useLayoutEffect, useMemo } from "react";

import {
	AdditionalTariff,
	CarClass,
	CarPark,
	Executor,
	ExecutorGroup,
	ExecutorRate,
	CustomerBalanceRule,
	ExecutorPenalty,
	ExecutorRating,
	DispatcherRate,
	OrderSettings,
	Role,
	Sector,
	Service,
	Tariff,
	TaxiService,
} from "../../services";
import { ExecutorSubscribeType } from "../../services/Executor";
import { useTypedDispatch, useTypedSelector } from "../../redux/store";
import { preferences, globalState } from "../../redux/reducers";
import taxiServices from "../../redux/reducers/taxiServices";
import additionalTariffs from "../../redux/reducers/additionalTariffs";
import order from "../../redux/reducers/settings/order";
import getCompanies from "../../redux/services/Company/getCompanies";
import setRates from "../../redux/actions/Preferences/Rates/setRates";
import {
	useModelSubscribe,
	useModelSubscribeOld,
	useTaxiServiceFilterAccessControl,
} from "../../hooks";
import PriceZone from "../../services/PriceZone/PrizeZone";
import Parking from "../../services/Parking2";

const BaseModelsLoader: React.FC<BaseModelsLoader.Props> = () => {
	const dispatch = useTypedDispatch();
	const { accessTaxiServicesIds, subTaxiServiceFilterAccess } =
		useTaxiServiceFilterAccessControl<Executor.Model>();

	const { connected, authorized } = useTypedSelector((state) => state.prpc);
	const language = useTypedSelector((state) => state.session.language);

	const canSubscribe: boolean = useMemo(
		() => connected && authorized,
		[connected, authorized],
	);

	const subscribeTaxiService = useModelSubscribe({}, TaxiService, {
		debounce: 300,
		onUpdateModels: (data) => {
			dispatch(taxiServices.actions.setModels(data || []));
		},
	});
	const ratesSubscription = useModelSubscribe({}, Tariff);
	useModelSubscribe({}, CarClass, {
		debounce: 300,
		onUpdateModels: (data) => {
			dispatch(preferences.carClasses.data.setModels(data || []));
		},
	});
	const subscribeService = useModelSubscribe({}, Service);
	const subscribeRole = useModelSubscribe({ language }, Role);
	const subscribeExecutorGroup = useModelSubscribe(
		{ lang: language },
		ExecutorGroup,
	);
	const subscribeExecutorRate = useModelSubscribe(
		{ lang: language },
		ExecutorRate,
	);
	const subscribeCustomerBalanceRule = useModelSubscribe(
		{ lang: language },
		CustomerBalanceRule,
	);
	const subscribeExecutorPenalty = useModelSubscribe(
		{ lang: language },
		ExecutorPenalty,
	);
	const subscribeExecutorRating = useModelSubscribe(
		{ lang: language },
		ExecutorRating,
	);
	const subscribeDispatcherRate = useModelSubscribe(
		{ lang: language },
		DispatcherRate,
	);
	const subscribeAdditionalTariff = useModelSubscribe({}, AdditionalTariff);
	const subscribeOrderSettings = useModelSubscribe(
		{ includeTaxiServices: true },
		OrderSettings,
	);
	const subscribeSector = useModelSubscribeOld({}, Sector);
	const subscribePriceZone = useModelSubscribe({}, PriceZone);
	// Очередь стоянок в Redux сразу из колбэка сокета, не через models → layout-эффект.
	// isAuth не ставить: в хуке из‑за ref подписка может не стартовать.
	useModelSubscribe({}, Parking, {
		debounce: 300,
		onUpdateModels: (data) => {
			dispatch(
				globalState.parking.data.setSubscriptionBasicData(data || []),
			);
		},
	});
	const subscribeExecutor = useModelSubscribe(
		{
			language,
			subscribeType: ExecutorSubscribeType.FRONT,
		},
		Executor,
	);

	const optionsCarPark = useMemo<CarPark.SubscribeOptions>(() => {
		const result: CarPark.SubscribeOptions = {
			language,
			taxiServiceIds: accessTaxiServicesIds,
		};

		return result;
	}, [accessTaxiServicesIds, language]);

	const subscribeCarPark = useModelSubscribe(optionsCarPark, CarPark);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeService.models : [];
		dispatch(preferences.services.data.setModels(models));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeService.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeSector?.cache || [] : [];
		dispatch(globalState.carSectors.data.setSubscriptionBasicData(models));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeSector?.cache]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribePriceZone?.models || [] : [];
		dispatch(globalState.priceZones.data.setSubscriptionBasicData(models));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribePriceZone?.models]);

	// Эффект не нужен: список кладёт onUpdateModels. При обрыве сессии стор этим не чистим.
	// useLayoutEffect(() => {
	// 	const models = canSubscribe ? subscribeParking?.models || [] : [];
	// 	dispatch(globalState.parking.data.setSubscriptionBasicData(models));
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [canSubscribe, subscribeParking?.models]);

	useLayoutEffect(() => {
		dispatch(setRates(ratesSubscription.models));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, ratesSubscription.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeExecutorGroup.models : [];
		dispatch(
			globalState.executorGroup.data.setSubscriptionBasicData(models),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeExecutorGroup.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeExecutorRate.models : [];
		dispatch(
			globalState.executorRate.data.setSubscriptionBasicData(models),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeExecutorRate.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeCustomerBalanceRule.models : [];
		dispatch(
			globalState.customerBalanceRule.data.setSubscriptionBasicData(
				models,
			),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeCustomerBalanceRule.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeExecutorPenalty.models : [];
		dispatch(
			globalState.executorPenalty.data.setSubscriptionBasicData(models),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeExecutorPenalty.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeExecutorRating.models : [];
		dispatch(
			globalState.executorRating.data.setSubscriptionBasicData(models),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeExecutorRating.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeDispatcherRate.models : [];
		dispatch(
			globalState.dispatcherRate.data.setSubscriptionBasicData(models),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeDispatcherRate.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeRole.models : [];
		dispatch(globalState.role.data.setSubscriptionBasicData(models));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeRole.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeCarPark.models : [];
		dispatch(globalState.carPark.data.setSubscriptionBasicData(models));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeCarPark.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeAdditionalTariff.models : [];
		dispatch(additionalTariffs.actions.setModels(models));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeAdditionalTariff.models]);

	useLayoutEffect(() => {
		const modelFirst =
			(canSubscribe && subscribeOrderSettings?.models?.[0]) || null;

		if (modelFirst) {
			dispatch(order.actions.set(modelFirst.settings));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeOrderSettings?.models]);

	useLayoutEffect(() => {
		const models = canSubscribe ? subscribeExecutor.models : [];

		const dataAfterAccessTaxiServer = subTaxiServiceFilterAccess({
			models,
			branchPath: "taxiService.id",
		});

		dispatch(
			globalState.executors.data.setSubscriptionBasicData(
				dataAfterAccessTaxiServer,
			),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSubscribe, subscribeExecutor.models, subscribeTaxiService.models]);

	useLayoutEffect(() => {
		dispatch(getCompanies());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return <></>;
};

declare namespace BaseModelsLoader {
	interface Props {}
}

export default BaseModelsLoader;
